/**
 * Terms of Service Page
 *
 * Public page - accessible without authentication. Linked from the login
 * footer and used as the terms of service URL for the CCMS mobile apps on the
 * Apple App Store and Google Play.
 */

import { Stack } from '@/components/ui/layout';
import { Title, Text, Anchor } from '@/components/ui/core';
import { Divider } from '@/components/ui/data-display';
import { IconFileText } from '@tabler/icons-react';
import LegalPageLayout from './LegalPageLayout';

export default function TermsOfServicePage() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated="30 June 2026"
      current="terms"
      icon={<IconFileText size={44} color="var(--mantine-color-indigo-6)" />}
    >
      {/* 1. Acceptance */}
      <section>
        <Title order={3} mb="sm">1. Acceptance of These Terms</Title>
        <Text size="sm">
          These Terms of Service ("Terms") govern your use of the CCMS apps and this web portal
          provided by Acts Christian Church ("Acts Church", "we", "our", or "us"). By accessing or
          using the apps, you agree to these Terms. If you do not agree, please do not use the apps.
        </Text>
      </section>

      <Divider />

      {/* 2. About the apps */}
      <section>
        <Title order={3} mb="sm">2. About the Apps</Title>
        <Text size="sm">
          The CCMS apps are a suite of church community and ministry tools (such as staff, tasks,
          chat, bookings, announcements, and resources) used to support the life and operations of
          Acts Christian Church.
        </Text>
      </section>

      <Divider />

      {/* 3. Eligibility & accounts */}
      <section>
        <Title order={3} mb="sm">3. Eligibility & Your Account</Title>
        <Stack gap="xs">
          <Text size="sm">
            3.1. The apps are provided for the members, staff, leaders, volunteers, and other
            authorised users of Acts Christian Church. You need an account issued or approved by
            the church to sign in.
          </Text>
          <Text size="sm">
            3.2. You are responsible for keeping your login credentials confidential and for all
            activity that occurs under your account.
          </Text>
          <Text size="sm">
            3.3. Please provide accurate information and notify us promptly of any unauthorised use
            of your account.
          </Text>
        </Stack>
      </section>

      <Divider />

      {/* 4. Acceptable use */}
      <section>
        <Title order={3} mb="sm">4. Acceptable Use</Title>
        <Text size="sm" mb="xs">You agree not to:</Text>
        <Stack gap="xs">
          <Text size="sm">- Use the apps for any unlawful, harmful, or fraudulent purpose</Text>
          <Text size="sm">- Post content that is abusive, harassing, defamatory, or infringing</Text>
          <Text size="sm">- Attempt to gain unauthorised access to the apps, accounts, or systems</Text>
          <Text size="sm">- Interfere with, disrupt, or place undue load on the apps</Text>
          <Text size="sm">- Misuse other users' personal information accessed through the apps</Text>
        </Stack>
      </section>

      <Divider />

      {/* 5. User content */}
      <section>
        <Title order={3} mb="sm">5. Your Content</Title>
        <Stack gap="xs">
          <Text size="sm">
            5.1. You retain ownership of the content you submit. You grant us a limited licence to
            host, store, and process that content solely to operate and provide the apps.
          </Text>
          <Text size="sm">
            5.2. You are responsible for the content you submit and confirm you have the right to
            share it.
          </Text>
          <Text size="sm">
            5.3. We may review, moderate, or remove content that breaches these Terms or applicable
            law.
          </Text>
        </Stack>
      </section>

      <Divider />

      {/* 6. Intellectual property */}
      <section>
        <Title order={3} mb="sm">6. Intellectual Property</Title>
        <Text size="sm">
          The apps, including their software, design, and content provided by us, are owned by Acts
          Christian Church or its licensors and are protected by applicable laws. You may not copy,
          modify, distribute, or create derivative works except as permitted by us or by law.
        </Text>
      </section>

      <Divider />

      {/* 7. Third-party services */}
      <section>
        <Title order={3} mb="sm">7. Third-Party Services</Title>
        <Text size="sm">
          The apps rely on third-party services (such as the Apple App Store, Google Play, and
          push notification providers). Your use of those services is subject to their own terms,
          and we are not responsible for them.
        </Text>
      </section>

      <Divider />

      {/* 8. Availability & changes */}
      <section>
        <Title order={3} mb="sm">8. Availability & Changes</Title>
        <Text size="sm">
          We aim to keep the apps available but provide them on an "as is" and "as available"
          basis. We may add, change, suspend, or discontinue features at any time without notice.
        </Text>
      </section>

      <Divider />

      {/* 9. Disclaimers & liability */}
      <section>
        <Title order={3} mb="sm">9. Disclaimers & Limitation of Liability</Title>
        <Stack gap="xs">
          <Text size="sm">
            9.1. To the maximum extent permitted by law, the apps are provided without warranties of
            any kind, whether express or implied.
          </Text>
          <Text size="sm">
            9.2. To the maximum extent permitted by law, Acts Christian Church will not be liable
            for any indirect, incidental, or consequential loss arising from your use of the apps.
          </Text>
        </Stack>
      </section>

      <Divider />

      {/* 10. Termination */}
      <section>
        <Title order={3} mb="sm">10. Suspension & Termination</Title>
        <Text size="sm">
          We may suspend or terminate your access to the apps if you breach these Terms, or where
          your relationship with the church ends. You may stop using the apps at any time.
        </Text>
      </section>

      <Divider />

      {/* 11. Governing law */}
      <section>
        <Title order={3} mb="sm">11. Governing Law</Title>
        <Text size="sm">
          These Terms are governed by the laws of the Republic of South Africa, and you agree to
          the jurisdiction of the South African courts.
        </Text>
      </section>

      <Divider />

      {/* 12. Changes to terms */}
      <section>
        <Title order={3} mb="sm">12. Changes to These Terms</Title>
        <Text size="sm">
          We may update these Terms from time to time. The "Last updated" date at the top shows
          when they were last revised. Continued use of the apps after changes means you accept the
          updated Terms.
        </Text>
      </section>

      <Divider />

      {/* 13. Contact */}
      <section>
        <Title order={3} mb="sm">13. Contact Us</Title>
        <Text size="sm">
          For questions about these Terms, please contact{' '}
          <Anchor href="mailto:developer@actschurch.co.za">developer@actschurch.co.za</Anchor>.
        </Text>
      </section>
    </LegalPageLayout>
  );
}
